import React, { useEffect, useState } from "react";
import { useRecoilValue, useResetRecoilState } from "recoil";
import { Box } from "@mui/material";
import { MyDocumentsEditFile } from "my-documents/components";
import {
  myDocumentsDocumentListMobileTableDef,
  myDocumentsDocumentListTableDef,
} from "my-documents/configs";
import { useMyDocumentsList } from "my-documents/hooks";
import { myDocumentsIsDocument } from "my-documents/states/is-document-state";
import { OcDataTable, OcIconForState, OcPageTransition } from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import { useOcTableState } from "oc/hooks";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";

interface MyDocumentsInfoCardProps {
  isUploader: boolean;
  contractCode: string;
  isUser: boolean;
  editDocument: any; // Replace 'any' with the appropriate type if known
  onCloseEdit: () => void;
  onEditDocument?: (document: any) => void; // Replace 'any' with the appropriate type if known
}

export const MyDocumentsInfoCard: React.FC<MyDocumentsInfoCardProps> = ({
  isUploader,
  contractCode,
  isUser,
  editDocument,
  onCloseEdit,
  onEditDocument,
}) => {
  const isMobile = useOcIsMobile();
  const {
    data: list,
    loading,
    error,
    deleteMyDocument,
    updateMyDocument,
    refetch
  } = useMyDocumentsList({ contractCode });

  const removeUpload = (item: { _id: string }) => {
    const { _id } = item;
    deleteMyDocument({ variables: { _id } });
  };

  const resetAddPermissionEmailState = useResetRecoilState(
    myDocumentsIsDocument,
  );
  let tableId = "myDocumentsUploaderTableData";
  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    sortField: "name",
    pageSize: 5,
  });
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const isDocument = useRecoilValue(myDocumentsIsDocument);

  useEffect(() => {
    if (isDocument) {
      setSelectedRowId(null);
      resetAddPermissionEmailState();
    }
  }, [isDocument, resetAddPermissionEmailState]);

  const handleTableDataChange = (state: typeof tableData) => {
    setTableData(state);
  };

  const onSelectRow = (row: { _id: string }) => {
    let selectedId = selectedRowId === row?._id ? null : row?._id;
    setSelectedRowId(selectedId);
  };

  const handleRowSelection = (row: { _id: string }) => {
    let selectedId = selectedRowId === row?._id ? null : row?._id;
    setSelectedRowId(selectedId);
  };

  const documentList = {
    rows: isUploader
      ? list
      : list?.filter((item: { userView: boolean }) => item.userView === true),
    total: list?.length,
  };

  const tableDef = isMobile
    ? myDocumentsDocumentListMobileTableDef
    : myDocumentsDocumentListTableDef;

  const filteredTableDef = {
    id: myDocumentsDocumentListTableDef?.id,
    columns: myDocumentsDocumentListTableDef?.columns?.filter(
      (column) =>
        !myDocumentsDocumentListMobileTableDef?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  if (editDocument) {
    return (
      <Box>
        <MyDocumentsEditFile
          document={editDocument}
          removeUpload={removeUpload}
          updateUpload={updateMyDocument}
          onClose={onCloseEdit}
          isMobile={isMobile}
          refetch={() => refetch()}
        />
      </Box>
    );
  }

  return (
    <OcPageTransition>
      <Box display="flex" flexDirection="column">
        {list?.length === 0 ? (
          <OcIconForState
            data={list}
            loading={loading}
            isMobile={isMobile}
            error={!!error}
            module="MyDocuments"
          />
        ) : (
          <OcDataTable
            tableDef={tableDef}
            tableId={tableId}
            tableData={tableData}
            setTableData={handleTableDataChange}
            data={documentList}
            loading={loading}
            selectedRowId={selectedRowId ?? undefined}
            onSelectRow={handleRowSelection}
            pagination={true}
            myDocuments={isMobile ? false : true}
            role={isUploader}
            removeUpload={removeUpload}
            onEditDocument={onEditDocument}
            localPaging={true}
            filteredTableDef={filteredTableDef}
            handleMoreButton={onSelectRow}
            inlineEdit={false}
            onRowSelection={() => { }}
            matchingMyTimeitems={false}
            isNotExaminer={isUser || !isUploader}
          />
        )}
      </Box>
    </OcPageTransition>
  );
};

export default MyDocumentsInfoCard;
